import React from "react";
import { Typography } from "common/components/Typography";
import { IconBadge } from "common/components/map/IconBadge";
import WbCloudyOutlinedIcon from "@mui/icons-material/WbCloudyOutlined";
import {
  ClimateRiskType,
  NoisePollutionLevel,
  PropertyNoisePollutionRisk,
} from "../../types/appraiser.types";
import colors from "common/styles/colors";
import { ClimateRiskTypesTranslations } from "../../helpers/comparableFields.helpers";
import {
  determineBackgroundColor,
  getNoisePollutionRiskDescription,
} from "../../helpers/climateRisk.helpers";
import { PropertyLocationMap } from "./PropertyLocationMap";
import {
  PropertyLocationDetailsType,
  PropertyLocationDetails,
} from "../../types/api.types";
import { PropertyClimateRiskWrapper } from "./PropertyClimateRiskWrapper";

interface PropertyNoisePollutionRiskContentProps {
  noisePollutionRisk?: PropertyNoisePollutionRisk;
  locations: PropertyLocationDetails[];
  onRegenerate?: () => void;
}

interface NoiseSectionProps {
  title: string;
  description: string;
  risk?: ClimateRiskType;
  minLevel?: number;
  maxLevel?: number | null;
  levels?: NoisePollutionLevel[];
  queryMethod?: PropertyNoisePollutionRisk["queryMethod"];
  location?: PropertyLocationDetails;
  onRegenerate?: () => void;
}

const NoiseSection: React.FC<NoiseSectionProps> = ({
  title,
  description,
  risk,
  minLevel,
  maxLevel,
  levels,
  queryMethod,
  location,
  onRegenerate,
}) => {
  return (
    <div
      style={{
        marginTop: "25px",
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <PropertyClimateRiskWrapper
        title={<Typography variant="h5">{title}</Typography>}
        description={<Typography variant="caption">{description}</Typography>}
        icon={
          <IconBadge
            width={40}
            height={40}
            backgroundColor={
              risk ? determineBackgroundColor(risk) : colors.grey
            }
          >
            <WbCloudyOutlinedIcon
              fontSize="medium"
              style={{ color: "white" }}
            />
          </IconBadge>
        }
        riskType={
          <Typography variant="h4">
            {risk ? ClimateRiskTypesTranslations[risk] : "Nenustatyta"}
          </Typography>
        }
        riskDescription={
          <Typography variant="caption">
            {risk
              ? getNoisePollutionRiskDescription(
                  minLevel,
                  maxLevel,
                  levels,
                  queryMethod
                )
              : "Triukšmo sklaida nenustatyta."}
          </Typography>
        }
      />
      {!!location && (
        <div style={{ width: "45%" }}>
          <PropertyLocationMap
            locationDetails={location}
            onRegenerate={onRegenerate}
            printableView={!onRegenerate}
          />
        </div>
      )}
    </div>
  );
};

export const PropertyNoisePollutionRiskContent: React.FC<
  PropertyNoisePollutionRiskContentProps
> = ({ noisePollutionRisk, locations, onRegenerate }) => {
  const roadLocation = locations.find(
    (location) =>
      location.locationType === PropertyLocationDetailsType.NOISE_POLLUTION_VILNIUS
  );
  const industryLocation = locations.find(
    (location) =>
      location.locationType ===
      PropertyLocationDetailsType.NOISE_POLLUTION_INDUSTRY_VILNIUS
  );

  return (
    <>
      <NoiseSection
        title="Triukšmo taršos rizika"
        description="Vertinama pagal triukšmo sklaidą nuo autotransporto."
        risk={noisePollutionRisk?.risk}
        minLevel={noisePollutionRisk?.minLevel}
        maxLevel={noisePollutionRisk?.maxLevel}
        levels={noisePollutionRisk?.levels}
        queryMethod={noisePollutionRisk?.queryMethod}
        location={roadLocation}
        onRegenerate={onRegenerate}
      />
      <NoiseSection
        title="Triukšmo taršos rizika"
        description="Vertinama pagal triukšmo sklaidą nuo pramonės."
        risk={noisePollutionRisk?.industryRisk}
        minLevel={noisePollutionRisk?.industryMinLevel}
        maxLevel={noisePollutionRisk?.industryMaxLevel}
        levels={noisePollutionRisk?.industryLevels}
        queryMethod={noisePollutionRisk?.queryMethod}
        location={industryLocation}
        onRegenerate={onRegenerate}
      />
    </>
  );
};
